import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LorryReceiptReviewService } from '../lorry-receipt-review.service';

type VerificationStep = 'login' | 'otp' | 'approved' | 'rejected';

@Component({
  selector: 'app-lr-verification',
  templateUrl: './lr-verification.component.html',
  styleUrls: ['./lr-verification.component.scss'],
  standalone: false
})
export class LrVerificationComponent implements OnInit, OnDestroy {
  accessCode = '';
  queryParams: Record<string, string> = {};
  contactInput = '';
  errorMessage = '';
  isLoading = false;
  isSubmitting = false;
  isResending = false;
  step: VerificationStep = 'login';

  lorryReceipt: any = null;
  ewayBills: any[] = [];
  items: any[] = [];
  attachments: any[] = [];

  otpDigits: string[] = ['', '', '', '', '', ''];
  otpError = '';
  remainingTime = '00:30';
  canResendOtp = false;

  @ViewChildren('otpBox') otpBoxes: QueryList<ElementRef<HTMLInputElement>>;

  private remainingSeconds = 30;
  private otpInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private lrReviewService: LorryReceiptReviewService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.queryParams = { ...params };
      this.accessCode = params['a'] || '';
      console.log('accessCode', this.accessCode);
      if (!this.accessCode) {
        this.isLoading = false;
        this.errorMessage = 'Access code is required';
        return;
      }

      this.loadLorryReceipt();
    });
  }

  private loadLorryReceipt(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.lorryReceipt = null;

    this.lrReviewService.getByAccessCode(this.accessCode).subscribe({
      next: (response) => {
        this.isLoading = false;
        const data = response?.data || response;
        this.lorryReceipt = data?.lorryReceipt || null;
        this.ewayBills = data?.ewayBills || [];
        this.items = data?.items || [];
        this.attachments = data?.attachments || [];

        if (!this.lorryReceipt) {
          this.errorMessage = 'Lorry receipt not found';
          return;
        }

        if (this.isApprovedStatus(this.lorryReceipt.status)) {
          this.step = 'approved';
        } else if (this.isRejectedStatus(this.lorryReceipt.status)) {
          this.step = 'rejected';
        } else {
          this.step = 'login';
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.lorryReceipt = null;
        this.errorMessage = this.mapReviewError(error);
      }
    });
  }

  get lrNumber(): string {
    return this.lorryReceipt?.lorryReceiptNumber || '-';
  }

  get fromLocation(): string {
    return this.lorryReceipt?.fromTradeName || this.lorryReceipt?.fromAddress || '-';
  }

  get toLocation(): string {
    return this.lorryReceipt?.toTradeName || this.lorryReceipt?.toAddress || '-';
  }

  get truckNumber(): string {
    const value = this.lorryReceipt?.vehicleNumber || '';
    return value ? value.toUpperCase() : '-';
  }

  get reportedTime(): string {
    return this.formatReportedTime(this.lorryReceipt?.reportingTime);
  }

  get invoiceNumber(): string {
    const docNos = (this.ewayBills || [])
      .map((bill) => bill?.docNo)
      .filter((value) => !!value);

    return docNos.length ? docNos.join(',\n') : '-';
  }

  get invoiceValue(): string {
    if (!this.ewayBills?.length) {
      return '-';
    }

    const amount = this.ewayBills.reduce((sum, bill) => {
      const value = Number(bill?.totalInvoiceValue);
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);

    return `₹ ${amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  private isApprovedStatus(status: string): boolean {
    const normalized = (status || '').toUpperCase();
    return normalized === 'APPROVED' || normalized === 'ACCEPTED';
  }

  private isRejectedStatus(status: string): boolean {
    return (status || '').toUpperCase() === 'REJECTED';
  }

  private formatReportedTime(value: string): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const hours = this.pad(date.getHours());
    const minutes = this.pad(date.getMinutes());
    const day = this.pad(date.getDate());
    const month = this.pad(date.getMonth() + 1);
    const year = date.getFullYear();

    return `${hours}:${minutes}; ${day}/${month}/${year}`;
  }

  private mapReviewError(error: any): string {
    const apiMessage = error?.error?.error || error?.error?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
      return apiMessage;
    }
    return 'Failed to load lorry receipt';
  }

  ngOnDestroy(): void {
    this.clearOtpTimer();
  }

  get canSendOtp(): boolean {
    return !!this.accessCode && !!this.lorryReceipt && !!this.contactInput.trim();
  }

  get canContinue(): boolean {
    return this.otpValue.length === 6 && !this.isSubmitting && !!this.lorryReceipt?.id;
  }

  get otpValue(): string {
    return this.otpDigits.join('');
  }

  get maskedContact(): string {
    return this.contactInput.trim();
  }

  sendOtp(): void {
    if (!this.canSendOtp) {
      return;
    }

    this.otpError = '';
    this.step = 'otp';
    this.resetOtpInputs();
    this.startOtpTimer();
    setTimeout(() => this.focusOtpBox(0));
  }

  goBackToLogin(): void {
    this.step = 'login';
    this.otpError = '';
    this.isSubmitting = false;
    this.resetOtpInputs();
    this.clearOtpTimer();
  }

  resendOtp(): void {
    if (!this.canResendOtp || this.isResending || !this.accessCode) {
      return;
    }

    this.isResending = true;
    this.otpError = '';

    this.lrReviewService.resendOtp(this.accessCode).subscribe({
      next: () => {
        this.isResending = false;
        this.resetOtpInputs();
        this.startOtpTimer();
        setTimeout(() => this.focusOtpBox(0));
      },
      error: (error) => {
        this.isResending = false;
        this.otpError = this.mapResendError(error);
      }
    });
  }

  onContinue(): void {
    if (!this.canContinue) {
      return;
    }

    const mobileNumber = this.contactInput.trim();
    const otp = this.otpValue;
    if (!mobileNumber || otp.length !== 6) {
      return;
    }

    this.otpError = '';
    this.clearOtpTimer();

    const authPayload = { mobileNumber, otp };
    try {
      sessionStorage.setItem(
        this.getAuthStorageKey(this.accessCode),
        JSON.stringify(authPayload)
      );
    } catch {
      // Ignore storage failures; router state still carries auth for this navigation.
    }

    this.router.navigate(['/lr-details'], {
      queryParams: { a: this.accessCode },
      state: authPayload
    });
  }

  private getAuthStorageKey(accessCode: string): string {
    return `lr_review_auth_${accessCode}`;
  }

  private mapResendError(error: any): string {
    const apiMessage = error?.error?.error || error?.error?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
      return apiMessage;
    }
    return 'Failed to resend OTP. Please try again.';
  }

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const digit = (input.value || '').replace(/\D/g, '').slice(-1);

    this.otpDigits[index] = digit;
    input.value = digit;
    this.otpError = '';

    if (digit && index < 5) {
      this.focusOtpBox(index + 1);
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      if (this.otpDigits[index]) {
        this.otpDigits[index] = '';
        return;
      }

      if (index > 0) {
        this.otpDigits[index - 1] = '';
        this.focusOtpBox(index - 1);
      }
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusOtpBox(index - 1);
    }

    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusOtpBox(index + 1);
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);

    if (!pasted) {
      return;
    }

    this.otpDigits = ['', '', '', '', '', ''];
    pasted.split('').forEach((digit, index) => {
      this.otpDigits[index] = digit;
    });

    const nextIndex = Math.min(pasted.length, 5);
    setTimeout(() => this.focusOtpBox(nextIndex));
  }

  trackByIndex(index: number): number {
    return index;
  }

  private resetOtpInputs(): void {
    this.otpDigits = ['', '', '', '', '', ''];
  }

  private startOtpTimer(seconds = 30): void {
    this.clearOtpTimer();
    this.remainingSeconds = seconds;
    this.canResendOtp = false;
    this.updateRemainingTime();

    this.otpInterval = setInterval(() => {
      this.remainingSeconds -= 1;
      this.updateRemainingTime();

      if (this.remainingSeconds <= 0) {
        this.clearOtpTimer();
        this.canResendOtp = true;
      }
    }, 1000);
  }

  private updateRemainingTime(): void {
    const minutes = Math.floor(Math.max(this.remainingSeconds, 0) / 60);
    const seconds = Math.max(this.remainingSeconds, 0) % 60;
    this.remainingTime = `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private clearOtpTimer(): void {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
      this.otpInterval = null;
    }
  }

  private focusOtpBox(index: number): void {
    const box = this.otpBoxes?.toArray()?.[index];
    box?.nativeElement?.focus();
    box?.nativeElement?.select();
  }
}
